import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Home, Shield, Users, FileText } from 'lucide-react';

const CGU = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Conditions Générales d'Utilisation - Frelon33™ | Expert Anti-Nuisibles Gironde";
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-blue-50/30">
      {/* Hero Section CGU */}
      <section className="bg-gradient-to-r from-blue-600 to-green-500 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center frelon-slide-up">
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              Conditions Générales d'Utilisation
            </h1>
            <p className="text-xl font-medium opacity-90">
              Règles d'utilisation du site - <span className="frelon33-brand">Frelon33™</span>
            </p>
            <div className="mt-8">
              <Link
                to="/"
                className="frelon-btn frelon-btn-secondary inline-flex items-center space-x-2 px-6 py-3 bg-white text-blue-600 hover:bg-blue-50"
              >
                <Home className="h-5 w-5" />
                <span>Retour à l'accueil</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contenu des CGU */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="frelon-card border-0 frelon-depth-2">
            <div className="prose prose-lg max-w-none">
              
              <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-2xl mb-8 border border-blue-100">
                <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <FileText className="h-6 w-6 text-blue-600 mr-2" />
                  Préambule
                </h2>
                <p className="text-gray-700">
                  Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation du site 
                  <strong className="frelon33-brand ml-1">Frelon33™</strong>, accessible à l'adresse 
                  <strong> https://www.frelon33.com</strong>.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                1. Objet
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <p>
                  Le site <strong className="frelon33-brand">Frelon33™</strong> a pour objet de présenter 
                  les services de désinsectisation et de destruction de nids de frelons proposés par 
                  l'entreprise individuelle dirigée par Jessy Josselin Jordan Duchemin.
                </p>
                <p>
                  L'accès et l'utilisation du site sont soumis à l'acceptation et au respect des présentes CGU.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                2. Accès au site
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="frelon-card bg-green-50 border-green-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <Users className="h-5 w-5 text-green-600 mr-2" />
                    Utilisateurs
                  </h3>
                  <p className="text-gray-700">
                    Le site est accessible gratuitement à tout utilisateur disposant d'un accès à Internet.
                  </p>
                </div>
                
                <div className="frelon-card bg-blue-50 border-blue-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <Shield className="h-5 w-5 text-blue-600 mr-2" />
                    Disponibilité
                  </h3>
                  <p className="text-gray-700">
                    Nous nous efforçons de maintenir le site accessible 24h/24 et 7j/7.
                  </p>
                </div>
              </div>

              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <p>
                  <strong className="frelon33-brand">Frelon33™</strong> se réserve le droit de suspendre, 
                  interrompre ou limiter l'accès au site, notamment pour des opérations de maintenance, 
                  sans préavis ni justification.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                3. Utilisation du site
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <h3 className="text-xl font-semibold text-gray-900">Utilisation autorisée</h3>
                <p>
                  L'utilisateur s'engage à utiliser le site de manière loyale et conformément à sa destination. 
                  Il est autorisé à :
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Consulter les informations disponibles sur le site</li>
                  <li>Contacter <strong className="frelon33-brand">Frelon33™</strong> via les moyens mis à disposition</li>
                  <li>Demander un devis ou une intervention</li>
                </ul>
              </div>

              <div className="frelon-card bg-red-50 border-red-200 mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Utilisations interdites</h3>
                <p className="text-gray-700 mb-3">Il est strictement interdit de :</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Utiliser le site à des fins illégales ou non autorisées</li>
                  <li>Tenter de porter atteinte au fonctionnement du site</li>
                  <li>Reproduire, copier ou distribuer le contenu sans autorisation</li>
                  <li>Usurper l'identité de <strong className="frelon33-brand">Frelon33™</strong></li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                4. Propriété intellectuelle
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <p>
                  Tous les éléments du site (textes, images, logos, etc.) sont protégés par les droits 
                  de propriété intellectuelle et appartiennent à <strong className="frelon33-brand">Frelon33™</strong> 
                  ou à ses partenaires.
                </p>
                <p>
                  Toute reproduction, même partielle, est interdite sans autorisation écrite préalable.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                5. Responsabilité
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <h3 className="text-xl font-semibold text-gray-900">Limitation of responsabilité</h3>
                <p>
                  <strong className="frelon33-brand">Frelon33™</strong> ne peut être tenu responsable :
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Des dommages directs ou indirects liés à l'utilisation du site</li>
                  <li>Des interruptions temporaires du service</li>
                  <li>Des erreurs ou omissions dans le contenu</li>
                  <li>Des problèmes techniques indépendants de notre volonté</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                6. Données personnelles
              </h2>
              
              <div className="frelon-card bg-green-50 border-green-200 mb-8">
                <p className="text-gray-700">
                  Le traitement des données personnelles est régi par notre 
                  <Link to="/politique-confidentialite" className="text-green-600 hover:text-green-700 font-semibold ml-1">
                    Politique de Confidentialité
                  </Link>.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                7. Modification des CGU
              </h2>
              
              <div className="space-y-4 text-gray-700 leading-relaxed mb-8">
                <p>
                  <strong className="frelon33-brand">Frelon33™</strong> se réserve le droit de modifier 
                  les présentes CGU à tout moment. Les modifications prennent effet dès leur publication sur le site.
                </p>
                <p>
                  Il appartient à l'utilisateur de consulter régulièrement les CGU pour prendre connaissance 
                  des éventuelles modifications.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                8. Loi applicable et juridiction
              </h2>
              
              <div className="frelon-card bg-yellow-50 border-yellow-200 mb-8">
                <p className="text-gray-700">
                  Les présentes CGU sont régies par la loi française. En cas de litige, 
                  les tribunaux français seront seuls compétents.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-6 frelon-heading">
                9. Contact
              </h2>
              
              <div className="frelon-card bg-blue-50 border-blue-200 mb-8">
                <p className="text-gray-700">
                  Pour toute question relative aux présentes CGU, vous pouvez nous contacter :
                </p>
                <ul className="list-none space-y-2 mt-3 text-gray-700">
                  <li><strong>E-mail :</strong> frelon33000@gmail.com</li>
                  <li><strong>Téléphone :</strong> 06 76 58 30 38</li>
                </ul>
              </div>

              <div className="mt-12 text-center">
                <p className="text-sm text-gray-500 mb-4">
                  Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
                </p>
                <button
                  onClick={scrollToTop}
                  className="frelon-btn frelon-btn-primary inline-flex items-center space-x-2 px-6 py-3"
                >
                  <ArrowUp className="h-5 w-5" />
                  <span>Retour en haut</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CGU;